import { useQuery } from '@tanstack/react-query';
import { useApi, type ArrivalStepKind, type PropertyGroupResponse } from '@/shared/api';
import { useArrivalGuidePreview } from '@/features/properties/arrival-guide';
import { propertyKeys } from '@/features/properties/queries';
import type { ArrivalStep } from '@/features/guest/useGuestSession';

/**
 * Eine Sammlung so, wie dieser Gast sie bekommt: geerbt, im Apartment überschrieben und eigene
 * Schritte — vom Server gerendert, Codes dabei verdeckt.
 */
function useApartmentGuide(
  apartmentId: string | undefined,
  kind: ArrivalStepKind,
  locale: 'de' | 'en',
  enabled: boolean,
) {
  const api = useApi();
  const list = useQuery({
    queryKey: ['properties', apartmentId ?? '', 'arrival-steps', kind],
    queryFn: () => api.arrivalSteps.listForApartment(apartmentId!, kind),
    enabled: !!apartmentId && enabled,
  });
  const source = list.data?.steps ?? [];
  const rendered = useArrivalGuidePreview(
    { kind: 'apartment', id: apartmentId ?? '' },
    source,
    apartmentId ?? null,
    locale,
  );
  const steps: ArrivalStep[] = source.map((step, index) => ({
    id: step.id,
    title: rendered[index]?.data?.title ?? step.titleDe,
    body: rendered[index]?.data?.body ?? null,
    isLocked: rendered[index]?.data?.isTimeLocked ?? step.isTimeLocked,
    availableFrom: null,
    images: [],
  }));
  return { steps, failed: list.isError || rendered.some((result) => result.isError) };
}

/**
 * Was die Portal-Vorschau für ein Apartment braucht — aus denselben Quellen wie das Portal, nie
 * aus einer Kopie. Wer eine Quelle nicht lesen darf, bekommt ihren Baustein nicht zu sehen.
 */
export function usePortalPreviewData(
  group: PropertyGroupResponse,
  apartmentId: string | undefined,
  locale: 'de' | 'en',
  canReadSources: boolean,
) {
  const api = useApi();
  const apartment = useQuery({
    queryKey: propertyKeys.detail(apartmentId ?? ''),
    queryFn: () => api.properties.get(apartmentId!),
    enabled: !!apartmentId && canReadSources,
    retry: false,
  });
  const parking = useQuery({
    queryKey: ['property-groups', group.id, 'parking-spots'],
    queryFn: () => api.parking.listSpots(group.id),
    enabled: canReadSources,
  });
  const arrival = useApartmentGuide(apartmentId, 'Arrival', locale, canReadSources);
  const alternatives = useApartmentGuide(
    apartmentId,
    'ParkingAlternatives',
    locale,
    canReadSources,
  );

  // Was nicht geladen werden konnte, fehlt in der Vorschau — und das wird gesagt, nicht verschwiegen.
  const failed = [
    arrival.failed ? 'Anfahrt' : null,
    parking.isError || alternatives.failed ? 'Parken' : null,
    apartment.isError ? 'Apartment-Angaben (WLAN, Notfallkontakt)' : null,
  ].filter((part): part is string => !!part);

  return {
    apartment: apartment.data,
    steps: arrival.steps,
    alternatives: alternatives.steps,
    parking: parking.data,
    parkingFailed: parking.isError,
    failed,
  };
}
